import React, { lazy, Suspense, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation } from 'react-router-dom';

import Icon from 'components/Icon';
import Row from 'components/Layout/Row';
import Column from 'components/Layout/Column';
import Button from 'components/Button';
import KeyboardShortcuts from 'components/KeyboardShortcuts';
import Loading from 'components/Loading';

import { useTheme } from 'styles/theme';

import { convertGSToDownloadURL, getSumoTier } from 'utils';

import ExtensionButton from 'components/ExtensionButton';
import Profile from './Profile';
import GoBack from './GoBack';
import Help from './Help';

import {
  HeaderContainer,
  Separator,
  DrawerBodyStyle,
  Drawer,
  NotificationsDropdown,
} from './Header.styled';

const ProjectUsers = lazy(() => import('components/ProjectUsers'));
const AddNewPage = lazy(() => import('components/AddPage/AddNewPage'));
const PendingPayment = lazy(() => import('./PendingPayment'));
const ProjectOptions = lazy(() => import('./ProjectOptions'));
const PowerUp = lazy(() => import('./PowerUp'));
const WhatsNew = lazy(() => import('./WhatsNew'));
const Notifications = lazy(() => import('./Notifications'));

const Header = ({
  match,
  pathname,
  user,
  users = [],
  notifications,
  signOut,
  showAddProjectModal,
  showShareProjectModal,
  seenNotification,
  currentProjectTitle,
  addWebPage,
  isCurrentProjectArchived,
  currentProjectType,
  clearAllNotifications,
  helpSubmit,
  feedbackSending,
  feedbackSent,
  showContactModal,
  setSubmitFeedbackDropDown,
  showSubmitFeedback,
  linkClickHandler,
  currentProjectArchivedPagesLength,
  currentArchivedPages,
  showAddPageForm,
  canAddNewPage,
  pages,
  projects,
  notificationsOpenedOnce,
  subscriptions,
  doesTeamExist,
  toggleTeamAddModal,
  showWhatsNew,
  setShowWhatsNew,
  showKeyboardShortcuts,
  setShowKeyboardShortcuts,
  onIntegrateSlack,
  onIntegrateTrello,
  checkIsAppIntegrated,
  // toggleTeamDropdown,
  // showTeamDropdown,
  // teams,
  addImage,
  team,
  toggleNotificationSoundDispatch,
  onIntegrateAsana,
  currentProjectAccess,
  updateAllPagesToLatestVersion,
  billing,
  extension,
  deleteProject,
}) => {
  const [notificationsActive, setNotificationsActive] = useState(false);
  const [projectFilter, setProjectFilter] = useState('all');
  const [whatsNewContent, setWhatsNewContent] = useState([0]); // [0] - is loading; [] - not loading but empty; [...] not loading with content

  const location = useLocation();

  const theme = useTheme();

  const toggleButtonRef = useRef();

  const getSortedNotifications = () => {
    const { all } = notifications;
    if (!all) return [];
    let sorted =
      projectFilter === 'all'
        ? [...all]
        : all.filter((n) => n.context.projectID === projectFilter);
    const foundGroups = Array.from(
      new Set(
        sorted.map(
          (i) =>
            `${i.type}__${i.context.projectID}__${i.context.pageID}__${i.context.versionID}__${i.context.userID}`,
        ),
      ),
    );

    foundGroups.forEach((g) => {
      const [type, projectID, pageID, versionID, userID] = g.split('__');
      const matches = (i) =>
        (i.type === 'web-comment' || i.type === 'web-inspect') &&
        i.type === type &&
        i.context.projectID === projectID &&
        i.context.pageID === pageID &&
        i.context.versionID === versionID &&
        i.context.userID === userID;
      const foundInThisContext = sorted.filter((i) => matches(i));

      if (foundInThisContext.length > 0) {
        foundInThisContext.sort(
          (a, b) => b.createdAt.valueOf() - a.createdAt.valueOf(),
        );
        sorted = sorted.filter((i) => !matches(i));
        sorted.push({
          ...foundInThisContext[0],
          id: foundInThisContext.map((i) => i.id).join('__'),
          length:
            /* eslint-disable indent */
            type === 'web-inspect'
              ? foundInThisContext.reduce(
                  (a, { inspectChangesLength, context: { editsCount = 0 } }) =>
                    a + editsCount ? editsCount : inspectChangesLength,
                  0,
                )
              : foundInThisContext.length,
          /* eslint-enable indent */
        });
      }
    });
    sorted = sorted.sort(
      (a, b) => b.createdAt.valueOf() - a.createdAt.valueOf(),
    );
    return sorted;
  };

  const unNotifiedCount = () =>
    getSortedNotifications().reduce(
      (a, c) => (c.isNotified === true ? a : a + 1),
      0,
    ) || 0;

  const renderAppLogo = () => {
    const showIcon = match.path.startsWith('/project/:projectID/:pageID');

    if (team) {
      const { customBranding = false, isDisabled, theme: t } = team;

      if (
        isDisabled === false &&
        customBranding &&
        (showIcon ? t.iconURL !== '' : t.logoURL !== '')
      ) {
        if (showIcon) {
          return (
            <img
              src={
                t.iconURL.startsWith('/assets')
                  ? t.iconURL
                  : convertGSToDownloadURL(t.iconURL, false)
              }
              alt=""
              style={{ width: 20, height: 20, objectFit: 'contain' }}
            />
          );
        }

        return (
          <img
            src={
              t.logoURL.startsWith('/assets')
                ? t.logoURL
                : convertGSToDownloadURL(t.logoURL, false)
            }
            alt=""
            style={{ width: 75, height: 25, objectFit: 'contain' }}
          />
        );
      }

      return <Icon name={showIcon ? 'icon' : 'logo'} verticalAlign="middle" />;
    }

    return <Icon name={showIcon ? 'icon' : 'logo'} verticalAlign="middle" />;
  };

  const renderArchivedPagesCount = () => {
    if (
      match.path.startsWith('/project/:projectID/archived') &&
      currentProjectArchivedPagesLength > 0
    ) {
      const pagesCount = currentArchivedPages.filter(
        (p) => p.url !== 'image',
      ).length;

      const filesCount = currentArchivedPages.filter(
        (p) => p.url === 'image',
      ).length;

      const message = [];

      if (pagesCount) {
        message.push(`${pagesCount} ${pagesCount === 1 ? 'page' : 'pages'}`);
      }

      if (filesCount) {
        message.push(`${filesCount} ${filesCount === 1 ? 'file' : 'files'}`);
      }

      return (
        <div className="archived-pages">Archived: {message.join(', ')}</div>
      );
    }

    return null;
  };

  const showExtensionLink =
    !!window.chrome &&
    (currentProjectType === 'webapp' || pathname === '/projects') &&
    pathname.startsWith('/project') &&
    (pathname.startsWith('/project/') ? !!pages.length : true) && // Hide if no pages.
    !extension.installed &&
    extension.accessible;

  const showWebAppTrialNotice =
    !!window.chrome &&
    currentProjectType === 'webapp' &&
    pathname.startsWith('/project') &&
    pathname.startsWith('/project/') &&
    extension.installed &&
    extension.accessible;

  const showPendingPayment =
    !['/billing', '/upgrade', '/upgrade/confirm/:plan'].includes(
      location.pathname,
    ) &&
    (user.sumoLingID ? getSumoTier(billing) === false : true) &&
    subscriptions.some(
      (s) =>
        s.status === 'past_due' ||
        s.status === 'incomplete' ||
        s.status === 'unpaid' ||
        s.pending_update,
    );

  const getToggleButtonRect = () =>
    toggleButtonRef.current.getBoundingClientRect();

  const toggleNotificationsActive = () => {
    setNotificationsActive((state) => !state);
    notificationsOpenedOnce();
  };

  return (
    <>
      <div style={{ height: 55 }} />
      <HeaderContainer>
        <Row wrap="no-wrap">
          <Column>
            {(pathname.startsWith('/projects') ||
              match.path.startsWith('/project/:projectID/:pageID') ||
              match.path.startsWith('/team')) && (
              <a
                href="/projects"
                onClick={linkClickHandler}
                style={{ verticalAlign: 'middle' }}>
                {renderAppLogo()}
              </a>
            )}
            <GoBack match={match} team={team} />
            <Separator />
            {pathname.startsWith('/project') && (
              <>
                {match.path === '/project/:projectID' ||
                match.path === '/project/:projectID/archived' ? (
                  <span className="project-title tooltip-container">
                    <span>{currentProjectTitle}</span>
                    {currentProjectTitle?.length > 20 && (
                      <span
                        className="tooltip"
                        style={{
                          maxWidth: 'max-content',
                          top: -12,
                          left: 10,
                          textTransform: 'none',
                        }}>
                        {currentProjectTitle}
                      </span>
                    )}
                  </span>
                ) : (
                  <Link
                    to="/projects"
                    className="link team-link"
                    style={{ marginLeft: 3 }}>
                    Projects
                  </Link>
                )}
              </>
            )}
            {(match.path === '/projects' ||
              match.path === '/projects/archived') && (
              <Link to="/team" className="link team-link team-link--inactive">
                Team
              </Link>
            )}
            {(match.path === '/team' || match.path === '/team/:teamID') && (
              <>
                <Link
                  to="/projects"
                  className="link team-link team-link--inactive"
                  style={{ marginLeft: 3 }}>
                  Projects
                </Link>
                <Link
                  to={match.path === '/team/:teamID' ? match.url : '/team'}
                  className="link team-link"
                  style={{ marginLeft: 20 }}>
                  Team
                </Link>
                {/* <MenuDropdown>
                {showTeamDropdown && (
                  <Button
                    type="button"
                    onClick={toggleTeamDropdown}
                    className="popup-wrapper absolute-inset"
                  />
                )}
                <ul
                  className={showTeamDropdown ? 'active' : ''}
                  style={{ right: 'auto', left: -12, top: 20 }}>
                  {teams.map(({ id, name }, index) => (
                    <li key={id}>
                      <Link
                        to={`/team/${id}`}
                        className={`link${
                          match.params.teamID === id ? ' blue bold' : ''
                        }`}
                        onClick={toggleTeamDropdown}>
                        {name || `Team ${index + 1}`}
                      </Link>
                    </li>
                  ))}
                </ul>
              </MenuDropdown>
              <Button
                type="button"
                className="link team-link"
                onClick={teams?.length > 0 ? toggleTeamDropdown : nullFunction}>
                <span>Team</span>
                {teams?.length > 0 && (
                  <span
                    className="expand-triangle"
                    style={{
                      display: 'inline-block',
                      transform: `translateY(-4px) rotate(${
                        showTeamDropdown ? 180 : 0
                      }deg)`,
                    }}
                  />
                )}
              </Button> */}
              </>
            )}
            {/* eslint-disable indent */}
            {renderArchivedPagesCount()}
            {showAddPageForm &&
              !isCurrentProjectArchived &&
              match.path === '/project/:projectID' && (
                <>
                  {['web', 'demo'].includes(currentProjectType) && (
                    <Suspense fallback={null}>
                      <AddNewPage
                        addWebPage={addWebPage}
                        isNotHeader={false}
                        canAddNewPage={canAddNewPage}
                        projectPagesCount={user.projectPagesCount || 5}
                        pages={pages}
                        addImage={addImage}
                      />
                    </Suspense>
                  )}
                  {currentProjectType === 'webapp' && (
                    <span style={{ marginLeft: 25 }}>
                      Use Chrome extension to add a page
                    </span>
                  )}
                </>
              )}
            {/* eslint-enable indent */}
            {(match.path === '/billing' || match.path === '/profile') && (
              <ul className="profile-menu">
                {[
                  { id: 0, path: '/profile', label: 'Account' },
                  { id: 1, path: '/billing', label: 'Billing' },
                ].map(({ id, path, label }) => (
                  <li key={id}>
                    <Link
                      to={path}
                      className={match.path === path ? 'is-active' : ''}>
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            {match.path.startsWith('/upgrade') && (
              <span
                className="medium"
                style={{ fontSize: 16, verticalAlign: 'middle' }}>
                {match.path === '/upgrade/confirm/:plan'
                  ? 'Payment summary'
                  : 'Choose your plan to upgrade'}
              </span>
            )}
          </Column>
          <Column className="right">
            {showExtensionLink && <ExtensionButton />}

            {showWebAppTrialNotice && (
              <span className="status-tag project-access-tag trial-period tooltip-container">
                Trial Period
                <span
                  className="tooltip"
                  style={{
                    textTransform: 'none',
                    width: 200,
                    top: 20,
                    left: 40,
                    whiteSpace: 'normal',
                  }}>
                  Web app available for free till June 15, 2022
                </span>
              </span>
            )}

            {pathname.startsWith('/project/') && (
              <>
                {currentProjectAccess && (
                  <span className="status-tag days project-access-tag">
                    {currentProjectAccess.text}
                  </span>
                )}
                {users && users.length > 1 && (
                  <Suspense fallback={null}>
                    <ProjectUsers
                      users={users.filter((u) => u.uid !== user.uid && u.email)}
                      toggleUsersManager={showShareProjectModal}
                      className="header-project-users"
                    />
                  </Suspense>
                )}
                <Button
                  type="button"
                  className="project-sharing-button"
                  onClick={showShareProjectModal}>
                  Share
                </Button>
                {/* eslint-disable indent */}
                {currentProjectAccess &&
                  (currentProjectAccess.id === 'edit' ||
                    currentProjectAccess.id === 'owner') && (
                    <Suspense fallback={null}>
                      <ProjectOptions
                        updateAllPagesToLatestVersion={
                          updateAllPagesToLatestVersion
                        }
                        deleteProject={deleteProject}
                      />
                    </Suspense>
                  )}
                {/* eslint-enable indent */}
                <Separator style={{ margin: '0 20px' }} />
              </>
            )}
            {/* eslint-disable indent */}
            {typeof user.projectsCount !== 'undefined' &&
              user.projectsCount !== -1 &&
              projects?.length > 0 &&
              !user.sumoLingID &&
              (match.path === '/projects' ||
                match.path === '/projects/archived') && (
                <span
                  className="medium"
                  style={{ verticalAlign: 'middle', marginRight: 15 }}>
                  Get unlimited projects.&nbsp;
                  <Link to="/upgrade" className="link">
                    Upgrade now
                  </Link>
                </span>
              )}
            {/* eslint-enable indent */}
            {pathname.startsWith('/projects') && (
              <Suspense fallback={null}>
                <PowerUp
                  onIntegrateSlack={onIntegrateSlack}
                  onIntegrateTrello={onIntegrateTrello}
                  checkIsAppIntegrated={checkIsAppIntegrated}
                  onIntegrateAsana={onIntegrateAsana}
                  user={user}
                />
              </Suspense>
            )}
            {match.path === '/team/:teamID' && doesTeamExist && (
              <Button
                type="button"
                styled
                onClick={toggleTeamAddModal}
                style={{ marginRight: 30 }}>
                + Add members
              </Button>
            )}
            {notificationsActive && (
              <Button
                type="button"
                onClick={toggleNotificationsActive}
                className="absolute-inset"
                style={{ position: 'fixed', cursor: 'default', zIndex: '2' }}
              />
            )}
            <Button
              type="button"
              forwardRef={toggleButtonRef}
              onClick={toggleNotificationsActive}
              className={`notifications-toggle tooltip-container${
                notificationsActive ? ' is-active' : ''
              }`}>
              <Icon
                name="bell"
                fill={theme.colors.deepLightGrey}
                verticalAlign="middle"
                height="20px"
              />
              <span className="tooltip" style={{ top: -15, left: 15 }}>
                Notifications
              </span>
              {unNotifiedCount() > 0 && (
                <div
                  className={`notifications-toggle--dot${
                    unNotifiedCount() > 9 ? ' plus' : ''
                  }`}>
                  {unNotifiedCount() > 9 ? '9+' : unNotifiedCount()}
                </div>
              )}
            </Button>
            <NotificationsDropdown
              /* eslint-disable indent */
              style={
                toggleButtonRef.current
                  ? {
                      right:
                        window.innerWidth - getToggleButtonRect().right - 10,
                    }
                  : {}
              }
              /* eslint-enable indent */
              className={notificationsActive ? 'is-active' : ''}>
              {notificationsActive && (
                <Suspense
                  fallback={
                    <Loading
                      width="30px"
                      height="100px"
                      style={{ minWidth: 100 }}
                    />
                  }>
                  <Notifications
                    notifications={notifications}
                    currentProjectID={match.params.projectID}
                    seenNotification={seenNotification}
                    clearAllNotifications={clearAllNotifications}
                    projects={projects}
                    user={{ notificationSound: true, ...user }}
                    setOpenedOnce={notificationsOpenedOnce}
                    toggleNotificationSoundDispatch={
                      toggleNotificationSoundDispatch
                    }
                    getSortedNotifications={getSortedNotifications}
                    projectFilter={projectFilter}
                    setProjectFilter={setProjectFilter}
                    setNotificationsActive={setNotificationsActive}
                    notificationsActive={notificationsActive}
                  />
                </Suspense>
              )}
            </NotificationsDropdown>
            <Help
              helpSubmit={helpSubmit}
              feedbackSending={feedbackSending}
              feedbackSent={feedbackSent}
              isOpen={showSubmitFeedback}
              toggleSubmitFeedback={setSubmitFeedbackDropDown}
              showWhatsNew={showWhatsNew}
              setShowWhatsNew={setShowWhatsNew}
              showKeyboardShortcuts={showKeyboardShortcuts}
              setShowKeyboardShortcuts={setShowKeyboardShortcuts}
            />
            <div className="add-project-button-container">
              {Array.isArray(projects) && pathname.startsWith('/projects') && (
                <Button
                  type="button"
                  styled
                  onClick={showAddProjectModal}
                  title="Create project"
                  style={{
                    marginLeft: 25,
                    verticalAlign: 'middle',
                    padding: '6.5px 15px',
                  }}>
                  + Create Project
                </Button>
              )}
            </div>
            <Profile
              user={user}
              signOut={signOut}
              showContactModal={showContactModal}
              linkClickHandler={linkClickHandler}
            />
            {showWhatsNew && <DrawerBodyStyle />}
            <Drawer
              style={
                showWhatsNew
                  ? { transform: 'translateX(0)' }
                  : { transform: 'translateX(105%)' }
              }>
              {showWhatsNew && (
                <Suspense fallback={<Loading />}>
                  <WhatsNew
                    isActive={showWhatsNew}
                    setShowWhatsNew={setShowWhatsNew}
                    whatsNewContent={whatsNewContent}
                    setWhatsNewContent={setWhatsNewContent}
                  />
                </Suspense>
              )}
            </Drawer>
            <KeyboardShortcuts
              isActive={showKeyboardShortcuts}
              setShowKeyboardShortcuts={setShowKeyboardShortcuts}
            />
          </Column>
        </Row>
      </HeaderContainer>
      {showPendingPayment && (
        <Suspense fallback={null}>
          <PendingPayment />
        </Suspense>
      )}
    </>
  );
};

Header.propTypes = {
  match: PropTypes.object.isRequired,
  pathname: PropTypes.string.isRequired,
  user: PropTypes.object.isRequired,
  users: PropTypes.array,
  notifications: PropTypes.object.isRequired,
  signOut: PropTypes.func.isRequired,
  showAddProjectModal: PropTypes.func.isRequired,
  showShareProjectModal: PropTypes.func.isRequired,
  showAddPageForm: PropTypes.bool.isRequired,
  seenNotification: PropTypes.func.isRequired,
  currentProjectTitle: PropTypes.string,
  addWebPage: PropTypes.func.isRequired,
  isCurrentProjectArchived: PropTypes.bool,
  currentProjectType: PropTypes.string,
  clearAllNotifications: PropTypes.func.isRequired,
  helpSubmit: PropTypes.func.isRequired,
  feedbackSending: PropTypes.bool.isRequired,
  feedbackSent: PropTypes.bool.isRequired,
  showSubmitFeedback: PropTypes.bool.isRequired,
  showContactModal: PropTypes.func.isRequired,
  setSubmitFeedbackDropDown: PropTypes.func.isRequired,
  linkClickHandler: PropTypes.func.isRequired,
  currentProjectArchivedPagesLength: PropTypes.number.isRequired,
  currentArchivedPages: PropTypes.array.isRequired,
  canAddNewPage: PropTypes.bool.isRequired,
  pages: PropTypes.array.isRequired,
  projects: PropTypes.array,
  notificationsOpenedOnce: PropTypes.func.isRequired,
  subscriptions: PropTypes.array.isRequired,
  doesTeamExist: PropTypes.bool.isRequired,
  toggleTeamAddModal: PropTypes.func.isRequired,
  showWhatsNew: PropTypes.bool.isRequired,
  setShowWhatsNew: PropTypes.func.isRequired,
  showKeyboardShortcuts: PropTypes.bool.isRequired,
  setShowKeyboardShortcuts: PropTypes.func.isRequired,
  // toggleTeamDropdown: PropTypes.func.isRequired,
  // showTeamDropdown: PropTypes.bool.isRequired,
  // teams: PropTypes.array.isRequired,
  addImage: PropTypes.func.isRequired,
  onIntegrateSlack: PropTypes.func,
  onIntegrateTrello: PropTypes.func,
  checkIsAppIntegrated: PropTypes.func,
  team: PropTypes.object,
  toggleNotificationSoundDispatch: PropTypes.func.isRequired,
  onIntegrateAsana: PropTypes.func.isRequired,
  currentProjectAccess: PropTypes.object,
  updateAllPagesToLatestVersion: PropTypes.func.isRequired,
  billing: PropTypes.object,
  extension: PropTypes.shape({
    installed: PropTypes.bool.isRequired,
    accessible: PropTypes.bool.isRequired,
  }).isRequired,
  deleteProject: PropTypes.func.isRequired,
};

export default Header;
